/**
 * Google Maps API Loader
 *
 * @package
 */

/**
 * Internal dependencies
 */
import mapsapi from './mapsapi';
import getSettings from './getsettings';
import getGeoJson from './getgeojson';
import { apply } from '../places';

/**
 * Load the maps
 *
 * @param {NodeListOf<HTMLElement>} maps The maps to load
 */
const load = ( maps: NodeListOf< HTMLElement > ) => {
	/**
	 * The Google Maps API loader
	 *
	 * @param {Function} loader The loader function
	 */
	mapsapi().then( async ( loader ) => {
		/**
		 * Load the maps library
		 *
		 * @param {typeof google.maps.Map} Map The Google maps library type
		 */
		loader.importLibrary( 'maps' ).then( ( { Map } ) => {
			/**
			 * Loop through the map elements
			 *
			 * @param {HTMLElement} mapEl The map element
			 */
			for ( const mapEl of maps ) {
				/**
				 * Get the page and block from the parent element data attributes
				 *
				 * @param {string} page  The page ID
				 * @param {string} block The block ID
				 */
				const page =
					mapEl.closest< HTMLElement >( '.wp-stockist-map' )?.dataset
						.page ?? '';
				const block =
					mapEl.closest< HTMLElement >( '.wp-stockist-map' )?.dataset
						.block ?? '';

				/**
				 * Get the map settings
				 *
				 * @param {Settings} settings The map settings
				 */
				getSettings( page, block ).then( ( settings ) => {
					/**
					 * The Google map instance
					 *
					 * @param {google.maps.Map} map The map instance
					 */
					const map = new Map( mapEl, settings.settings );

					/**
					 * The Google maps info window instance for displaying place details
					 *
					 * @param {google.maps.InfoWindow} infowindow The info window
					 */
					const infowindow = new google.maps.InfoWindow();

					// Apply the places module to the map
					infowindow.addListener( 'domready', () => {
						apply( mapEl, map );
					} );

					/**
					 * Set the map styles and bounds
					 */
					map.mapTypes.set(
						'styled_map',
						new google.maps.StyledMapType( settings.styles )
					);
					map.setMapTypeId( 'styled_map' );
					map.fitBounds( settings.bounds, 0 );

					map.data.setStyle( ( feature: any ) => {
						const datastyle: google.maps.Data.StyleOptions = {
							title: feature.getProperty( 'title' ),
						};

						if ( settings.marker ) {
							datastyle.icon = settings.marker;
						}

						return datastyle;
					} );

					/**
					 * The map GeoJSON data
					 *
					 * @param {Geojson} geojson The map data
					 */
					getGeoJson( page, block ).then( ( geojson ) => {
						// Add the GeoJSON data to the map
						map.data.addGeoJson( geojson );
						map.data.setMap( map );

						// Add click listeners to open and close the infowindow
						map.data.addListener(
							'click',
							( event: google.maps.Data.MouseEvent ) => {
								infowindow.setOptions( {
									content: event.feature.getProperty(
										'content'
									) as string,
									ariaLabel: event.feature.getProperty(
										'title'
									) as string,
									position: event.latLng,
								} );
								infowindow.open( {
									map,
								} );
							}
						);
						map.addListener( 'click', () => {
							infowindow.close();
						} );
					} );
				} );
			}
		} );
	} );
};

export default load;
